import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, AlertCircle, CalendarClock, HeartPulse } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { ADMIN_EMPLOYEES } from "@/constants/testIds";

const FILTER_LABELS = {
  pending_policies: "Con políticas pendientes",
  has_incidents: "Con incidencias",
  high_risk: "Riesgo alto (3+ incidencias)",
  unscheduled_vacation: "Con vacaciones acumuladas sin programar",
  missing_emergency_contact: "Sin contacto de emergencia capturado",
};

const TAB_INTENT_LABELS = {
  baja: "Elige un empleado para ir directo a su pestaña Baja (Aviso de Rescisión Asistido)",
  checkpoint: "Elige un empleado para ir directo a su pestaña Considera Antes de Decidir",
};

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const filter = searchParams.get("filter");
  const tab = searchParams.get("tab");

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data)).finally(() => setLoading(false));
  }, []);

  let filtered = employees.filter((e) => e.full_name.toLowerCase().includes(query.toLowerCase()));
  if (filter === "pending_policies") filtered = filtered.filter((e) => e.pending_policies > 0);
  if (filter === "has_incidents") filtered = filtered.filter((e) => e.incidents_count > 0);
  if (filter === "high_risk") filtered = filtered.filter((e) => e.incidents_count >= 3);
  if (filter === "unscheduled_vacation") filtered = filtered.filter((e) => e.unscheduled_vacation_days > 0);
  if (filter === "missing_emergency_contact") filtered = filtered.filter((e) => !e.contacto_emergencia_nombre);

  return (
    <AdminLayout>
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight">Empleados</h1>
          <Link data-testid={ADMIN_EMPLOYEES.newEmployeeButton} to="/admin/empleados/nuevo" className="flex items-center gap-2 bg-terracotta text-white font-semibold px-5 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all">
            <Plus className="w-4 h-4" /> Nuevo empleado
          </Link>
        </div>

        {filter && (
          <div data-testid="employee-list-active-filter" className="mb-4">
            <span className="inline-flex items-center gap-2 text-xs font-medium text-petroleum bg-accent px-3 py-1.5 rounded-full">
              Filtro: {FILTER_LABELS[filter] || filter}
              <Link to="/admin/empleados" className="underline">Quitar</Link>
            </span>
          </div>
        )}

        {TAB_INTENT_LABELS[tab] && (
          <div data-testid="employee-list-tab-intent-banner" className="mb-4">
            <span className="inline-flex items-center gap-2 text-xs font-medium text-terracotta bg-terracotta/10 px-3 py-1.5 rounded-full">
              {TAB_INTENT_LABELS[tab]}
            </span>
          </div>
        )}

        <div className="relative mb-6 max-w-sm">
          <Search className="w-4 h-4 text-charcoal/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid={ADMIN_EMPLOYEES.searchInput} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar empleado..." className="pl-9 bg-white" />
        </div>

        {loading && <p className="text-charcoal/60">Cargando expedientes...</p>}

        <div className="bg-card border border-petroleum/10 rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(14,79,79,0.06)]">
          {filtered.map((emp) => (
            <Link
              key={emp.id}
              to={tab ? `/admin/empleados/${emp.id}?tab=${tab}` : `/admin/empleados/${emp.id}`}
              data-testid={`${ADMIN_EMPLOYEES.employeeRow}-${emp.id}`}
              className="flex items-center justify-between px-6 py-4 border-b border-petroleum/5 last:border-0 hover:bg-accent/50 transition-colors"
            >
              <div>
                <p className="font-semibold text-charcoal">{emp.full_name}</p>
                <p className="text-xs text-charcoal/50">{emp.position} · {emp.work_center}</p>
              </div>
              <div className="flex items-center gap-4">
                {emp.pending_policies > 0 && (
                  <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
                    <AlertCircle className="w-3 h-3" /> {emp.pending_policies} pendiente(s)
                  </span>
                )}
                {emp.incidents_count >= 3 && (
                  <span className="text-xs font-medium text-red-700 bg-red-50 px-2.5 py-1 rounded-full">{emp.incidents_count} incidencias</span>
                )}
                {emp.pending_vacation_requests > 0 && (
                  <span className="flex items-center gap-1 text-xs font-medium text-terracotta bg-terracotta/10 px-2.5 py-1 rounded-full">
                    <CalendarClock className="w-3 h-3" /> Vacaciones
                  </span>
                )}
                {emp.unscheduled_vacation_days > 0 && (
                  <span className="flex items-center gap-1 text-xs font-medium text-petroleum bg-petroleum/10 px-2.5 py-1 rounded-full">
                    <CalendarClock className="w-3 h-3" /> {emp.unscheduled_vacation_days} sin programar
                  </span>
                )}
                {!emp.contacto_emergencia_nombre && (
                  <span data-testid={`employee-list-missing-ec-badge-${emp.id}`} className="flex items-center gap-1 text-xs font-medium text-charcoal/50 bg-charcoal/5 px-2.5 py-1 rounded-full">
                    <HeartPulse className="w-3 h-3" /> Sin contacto de emergencia
                  </span>
                )}
                <span className="text-sm text-charcoal/60">{emp.vacation_balance} días vac.</span>
              </div>
            </Link>
          ))}
          {!loading && filtered.length === 0 && <p className="px-6 py-8 text-center text-charcoal/50">No hay empleados que coincidan.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
